import React, { useCallback, useEffect, useState } from 'react';
import { getInvoiceStripeUrl, listOpenInvoices } from '@/services/api';
import { safeHttpsUrl } from '@/services/links';
import { parseHostedError } from '@/services/errors';
import { formatDate, formatMoney } from '@/format';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';
import CtaButton from '@/components/CtaButton';

/**
 * The single-invoice path (`/invoice?t=` links):
 * find the oldest open invoice and hand the parent to Stripe's hosted
 * invoice page, where payment happens entirely on Stripe. An invoice with a
 * bank debit still settling (`payment_in_flight`) is never handed over: one
 * live attempt per invoice, so the API refuses a second payment against it
 * even when the debit covers only part of the balance. The screen says so,
 * and names what is actually on its way.
 */
/**
 * What the live debit is actually for.
 *
 * `amount_remaining_cents` is the WHOLE invoice, which is what this screen
 * used to show: a parent who paid a custom amount for one week was told his
 * entire balance was processing. `payment_in_flight_cents` is the amount
 * being paid; the fallback keeps the screen correct against an API that has
 * not been deployed yet.
 */
const inFlightCents = (invoice) => invoice?.payment_in_flight_cents ?? invoice?.amount_remaining_cents ?? 0;

/** The part of the balance no debit covers — still owed, not yet payable. */
const remainderCents = (invoice) => Math.max(0, (invoice?.amount_remaining_cents ?? 0) - inFlightCents(invoice));

const InvoiceRedirectScreen = ({ studentId, studentName, onBack }) => {
	const [state, setState] = useState('loading'); // loading | empty | processing | verify | error
	const [error, setError] = useState(null);
	const [processing, setProcessing] = useState(null);
	const [verifying, setVerifying] = useState(null);

	const go = useCallback(async () => {
		setState('loading');
		setError(null);
		try {
			const invoices = await listOpenInvoices(studentId);
			const open = (Array.isArray(invoices) ? invoices : []).sort((a, b) =>
				String(a.due_date ?? '').localeCompare(String(b.due_date ?? ''))
			);

			// A payment WAITING ON THE PARENT comes first, and must never be
			// sent to a fresh payment page. The invoice is still payable --
			// nothing is on its way, so no hold -- which is exactly why it
			// would otherwise land here and have the parent start a SECOND
			// payment against the same balance, leaving two waiting on the
			// same bill. The route back only existed on the post-payment
			// screen; once that closed there was no way to the code step.
			const awaiting = open.find((invoice) => invoice.verification_pending && invoice.verification);
			if (awaiting) {
				setVerifying(awaiting);
				setState('verify');
				return;
			}

			const payable = open.filter((invoice) => !invoice.payment_in_flight);
			if (!payable.length) {
				const inFlight = open.find((invoice) => invoice.payment_in_flight);
				setProcessing(inFlight ?? null);
				setState(inFlight ? 'processing' : 'empty');
				return;
			}

			const { url } = await getInvoiceStripeUrl(payable[0].id);
			if (!url) throw { stripeMessage: 'The invoice’s payment page isn’t ready yet — try again in a moment.' };
			window.location.replace(url);
		} catch (err) {
			setError(parseHostedError(err).message);
			setState('error');
		}
	}, [studentId]);

	useEffect(() => {
		go();
	}, [go]);

	const backLink = onBack ? (
		<button type="button" onClick={onBack} className="w-full text-center text-[13px] font-semibold text-pd-blue-new">
			← Back to Auto-Pay
		</button>
	) : null;

	if (state === 'empty') {
		return (
			<div className="flex flex-col items-center gap-[18px] pt-[12px] text-center">
				<div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-pay-green-bg text-[32px]">
					🎉
				</div>
				<h1 className="text-[24px] font-extrabold text-pay-ink">You’re all paid up!</h1>
				<p className="text-[14px] leading-[21px] text-pay-muted">
					{studentName ?? 'Your student'} has no open invoices right now.
				</p>
				{backLink}
			</div>
		);
	}

	if (state === 'verify') {
		const verification = verifying?.verification ?? {};
		const url = safeHttpsUrl(verification.hosted_verification_url);
		const isCode = verification.microdeposit_type === 'descriptor_code';
		const arrival = verification.arrival_date ? formatDate(`${verification.arrival_date}T00:00:00`) : null;

		return (
			<div className="flex flex-col items-center gap-[18px] pt-[12px] text-center">
				<div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-pay-info-bg text-[32px]">
					🏦
				</div>
				<h1 className="text-[24px] font-extrabold text-pay-ink">Finish verifying your bank</h1>
				<p className="text-[14px] leading-[21px] text-pay-muted">
					A payment of {formatMoney(verifying.verification_pending_amount_cents, verifying.currency)} for{' '}
					{studentName ?? 'your student'} is waiting on you. Stripe sent{' '}
					{isCode ? 'a small deposit with a 6-digit code' : 'two small deposits'} to your account
					{arrival ? ` by ${arrival}` : ' within 1–2 business days'}. Enter {isCode ? 'the code' : 'the amounts'} to
					complete it — the invoice stays due until you do.
				</p>
				{url ? (
					<CtaButton onClick={() => window.open(url, '_blank', 'noopener')}>
						{isCode ? 'Enter the code' : 'Enter the deposits'}
					</CtaButton>
				) : null}
				{backLink}
			</div>
		);
	}

	if (state === 'processing') {
		return (
			<div className="flex flex-col items-center gap-[18px] pt-[12px] text-center">
				<div className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-pay-summary text-[32px]">
					🏦
				</div>
				<h1 className="text-[24px] font-extrabold text-pay-ink">Your payment is processing</h1>
				<p className="text-[14px] leading-[21px] text-pay-muted">
					A bank payment of {formatMoney(inFlightCents(processing), processing.currency)} for{' '}
					{studentName ?? 'your student'} is on its way. Bank transfers take a few business days to settle
					{remainderCents(processing) > 0 ? (
						<>
							, and {formatMoney(remainderCents(processing), processing.currency)} of this invoice will still be owed
							once it does. You can pay that once this payment settles.
						</>
					) : (
						<>, and the invoice will show as paid once it does.</>
					)}
				</p>
				{backLink}
			</div>
		);
	}

	if (state === 'error') {
		return (
			<div className="flex flex-col gap-[18px] pt-[12px]">
				<ErrorBanner>{error}</ErrorBanner>
				<CtaButton onClick={go}>Try again</CtaButton>
				{backLink}
			</div>
		);
	}

	return <Spinner label="Finding your invoice…" />;
};

export default InvoiceRedirectScreen;
